import { faTrash } from '@fortawesome/pro-light-svg-icons';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Color from 'color';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button, Grid, Paper, styled, Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { FieldType } from '../../parsing/parser';
import { ErrorMessageText } from './common';
import SimpleTooltip from '../../../components/SimpleTooltip';
import { ControlledFieldControl } from '../layout/Element';
import type { FormFieldDef } from '../../types';
import type { Nullable } from '../../../types/common/utility';

type FieldListProps = {
  addButtonLabel?: string;
  name: string;
  schemaProps: any;
  error?: string;
  disabled?: boolean;
  maxItems: Nullable<number>;
  formId: string;
};

export const FieldList = (props: FieldListProps) => {
  const formContext = useFormContext();
  const fieldArrayMethods = useFieldArray({
    control: formContext.control,
    name: `${props.name}.values`,
  });

  const FIELD_TEMPLATE = {
    props: Object.fromEntries(
      props.schemaProps.required?.map((key: string) => [key, { value: '' }])
    ),
  };

  return (
    <React.Fragment>
      {props.addButtonLabel !== undefined && (
        <Button
          startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
          disabled={
            (props.maxItems &&
              fieldArrayMethods.fields.length >= props.maxItems) ||
            props.disabled
          }
          className="contrast-text"
          onClick={() => fieldArrayMethods.append(FIELD_TEMPLATE)}
          variant="outlined"
          style={{
            marginTop: '0.2rem',
            padding: '0.25rem 1rem',
          }}
        >
          {props.addButtonLabel}
        </Button>
      )}

      {fieldArrayMethods.fields.map((field, index) => {
        return (
          <StyledPaper elevation={2} key={field.id}>
            <Grid
              container
              columnSpacing={1}
              sx={{
                padding: '7px',
                position: 'relative',
                paddingRight: '22px',
              }}
            >
              {props.schemaProps.required.map((key: string) => {
                const fieldSchema = props.schemaProps.properties[key];

                const type =
                  fieldSchema.properties.value.format?.toUpperCase() ??
                  FieldType.Text;

                return (
                  <Grid
                    item
                    sm={2}
                    key={key}
                    alignItems="center"
                    alignContent="center"
                    sx={{
                      '& .MuiInputLabel-root': {
                        minHeight: '3rem',
                      },
                    }}
                  >
                    <ControlledFieldControl
                      name={`${props.name}.values.${index}.props.${key}`}
                      label={fieldSchema.label}
                      meta={
                        {
                          schema: fieldSchema,
                          type,
                          valueOptions: fieldSchema.properties.value.options,
                          text: fieldSchema.label,
                        } as FormFieldDef
                      }
                      disabled={props.disabled}
                      formId={props.formId}
                    />
                  </Grid>
                );
              })}

              <StyledBox>
                <RemoveItem
                  removeListItem={() => fieldArrayMethods.remove(index)}
                />
              </StyledBox>
            </Grid>
          </StyledPaper>
        );
      })}

      <ErrorMessageText>{props.error}</ErrorMessageText>
    </React.Fragment>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => {
  return {
    background:
      theme.palette.mode === 'dark'
        ? Color(theme.config.colors.darkGreyActive).darken(0.3).toString()
        : Color(theme.palette.background.active).lighten(0.065).toString(),
    borderRadius: 5,
    margin: '1rem 0',
    position: 'relative',
  };
});

const StyledBox = styled(Box)`
  right: 18px;
  top: 0px;
  position: absolute;
`;

type RemoveItemProps = {
  removeListItem: () => void;
};

export const RemoveItem = ({ removeListItem }: RemoveItemProps) => {
  return (
    <SimpleTooltip
      title="Remove"
      enterDelay={500}
      enterNextDelay={500}
      style={{
        marginLeft: 0,
        top: '50%',
        position: 'absolute',
      }}
      placement="top"
    >
      <IconButton
        className="contrast-text"
        style={{
          top: '50%',
          right: -7,
          position: 'absolute',
        }}
        onClick={removeListItem}
      >
        <FontAwesomeIcon
          style={{ fontSize: '1.25rem' }}
          icon={faTrash}
        />
      </IconButton>
    </SimpleTooltip>
  );
};
